"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { emptySavedLogin, readSavedLogin, saveLogin, clearSavedLogin, subscribeSavedLogin } from "@/lib/saved-login";
import { readStoredLoginRedirect } from "@/lib/login-redirect";
import Link from "next/link";

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
      <circle cx={12} cy={12} r={3} />
    </svg>
  );
}

function EyeOffIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
      <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
      <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
      <path d="m2 2 20 20" />
    </svg>
  );
}

const EXPIRED_MESSAGE = "Your session expired. Please sign in again to continue.";
const ERROR_MESSAGE: Record<string, string> = {
  invalid: "Email or password is incorrect.",
  missing: "Email and password are required.",
  account: "This account is not assigned to a company.",
};

export default function LoginForm({
  sessionExpired = false,
  errorCode,
  redirectTo = "",
}: {
  sessionExpired?: boolean;
  errorCode?: "invalid" | "missing" | "account";
  redirectTo?: string;
}) {
  const saved = useSyncExternalStore(subscribeSavedLogin, readSavedLogin, emptySavedLogin);
  const [showPassword, setShowPassword] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const restoreAttempted = useRef(false);
  const passwordRef = useRef<HTMLInputElement>(null);
  const typedPassword = useRef("");
  const [sessionMessage] = useState<string | null>(sessionExpired ? EXPIRED_MESSAGE : null);
  const errorMessage = errorCode ? ERROR_MESSAGE[errorCode] : "";
  const restoringSession = Boolean(
    sessionExpired && !errorCode && saved?.email && saved.password,
  );

  useEffect(() => {
    const input = passwordRef.current;
    if (!input || !typedPassword.current) return;
    if (input.value !== typedPassword.current) input.value = typedPassword.current;
  }, [showPassword]);

  useEffect(() => {
    if (
      restoreAttempted.current ||
      !sessionExpired ||
      errorCode ||
      !saved?.email ||
      !saved.password
    ) {
      return;
    }
    restoreAttempted.current = true;
    formRef.current?.requestSubmit();
  }, [errorCode, saved, sessionExpired]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const form = e.currentTarget;
    const redirectInput = form.querySelector<HTMLInputElement>('input[name="redirect"]');
    if (redirectInput && !redirectInput.value.trim()) {
      redirectInput.value = readStoredLoginRedirect();
    }
    const rememberInput = form.querySelector<HTMLInputElement>("#remember");
    const email = form.querySelector<HTMLInputElement>("#email")?.value.trim() ?? "";
    const password = form.querySelector<HTMLInputElement>("#password")?.value ?? "";
    // The box is uncontrolled so iOS can toggle it. Checked means save the password here.
    if (rememberInput?.checked) saveLogin(email, password);
    else clearSavedLogin();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-zinc-100 to-zinc-200">
      <div className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-xl">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-extrabold tracking-tight text-zinc-900">Connectz</h1>
          <p className="mt-1 text-sm text-zinc-500">Sign in to your account</p>
        </div>

        {sessionMessage && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
            {sessionMessage}
          </div>
        )}

        <form
          ref={formRef}
          key={saved?.email ?? "new"}
          method="post"
          action={
            redirectTo
              ? `/api/auth/login?redirect=${encodeURIComponent(redirectTo)}`
              : "/api/auth/login"
          }
          autoComplete="on"
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
        >
          <input type="hidden" name="redirect" defaultValue={redirectTo} />
          {/* Above the password field so the iOS password sheet cannot cover the tap. */}
          <label
            htmlFor="remember"
            className="flex min-h-12 cursor-pointer touch-manipulation items-center gap-3 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm font-medium text-zinc-800"
          >
            <input
              id="remember"
              name="remember"
              type="checkbox"
              value="yes"
              defaultChecked
              className="h-6 w-6 shrink-0 rounded border-zinc-400 accent-indigo-600"
            />
            Save login and stay signed in for 7 days
          </label>
          <p className="text-xs text-zinc-500">This phone stays signed in for 7 days after Sign In.</p>
          {restoringSession ? (
            <p className="text-xs font-semibold text-indigo-700">
              Restoring your saved sign-in…
            </p>
          ) : null}

          <div>
            <label htmlFor="email" className="mb-1 block text-xs font-semibold text-zinc-700">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              defaultValue={saved?.email ?? ""}
              required
              autoFocus
              autoComplete="username"
              className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm text-zinc-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <label htmlFor="password" className="text-xs font-semibold text-zinc-700">
                Password
              </label>
              <Link
                href="/forgot-password"
                className="text-xs font-medium text-indigo-600 hover:text-indigo-800 transition"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                ref={passwordRef}
                defaultValue={saved?.password ?? ""}
                required
                autoComplete="current-password"
                onInput={(e) => {
                  typedPassword.current = e.currentTarget.value;
                }}
                className="w-full rounded-lg border border-zinc-300 px-3 py-2 pr-10 text-sm text-zinc-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOffIcon className="h-4.5 w-4.5" /> : <EyeIcon className="h-4.5 w-4.5" />}
              </button>
            </div>
          </div>

          {errorMessage && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            className="w-full rounded-lg bg-indigo-600 py-2.5 text-sm font-bold text-white transition hover:bg-indigo-700"
          >
            Sign In
          </button>
        </form>
      </div>
    </div>
  );
}
